import { serviceElement, type WithElement } from './applies-to';
import type { GroundBeam, PileCap } from './foundation';
import { groundBeamTotals, pileCapTotals } from './foundation-totals';
import type { MeasureBy } from './geometry';
import { ownFactorsByKey, type ItemFactor } from './own-factor';
import type { PileCapType } from './pile-cap-type';
import type { RetainingColumn, RetainingWall } from './retaining';
import { retainingColumnTotals, retainingWallTotals } from './retaining-totals';
import type { Slab } from './slab';
import { slabTotals } from './slab-totals';
import type { Stair } from './stair';
import { stairTotals } from './stair-totals';
import { STEEL_GAUGES, type SteelRate } from './steel';
import type { StructuralElementKind } from './structural-element';

/**
 * O fator peça a peça na fundação (Fase 41, etapa 1 — F1, F7; RN-538, RN-539; ADR-185).
 *
 * Até aqui o bloco, o baldrame e o radier tinham um índice só por item na obra, que multiplicava
 * a medida somada de todas as peças. Agora cada peça pode ter o fator dela: o bloco, o do tipo e
 * o próprio do card por cima; o baldrame e o radier, o próprio do card. A conta é a do fator
 * próprio do cômodo (Fase 39, RN-512): a medida do serviço se reparte peça a peça pela medida de
 * cada uma, e cada parte leva o fator que vale nela, ou o índice da obra.
 *
 * ```text
 * concreto dos blocos = Σ medida do serviço × parte do bloco × (próprio ?? tipo ?? índice)
 * ```
 *
 * No serviço de aço, a parte de cada peça é o aço dela, e não o volume (RN-556): a peça armada a
 * 117 kg/m³ pesa mais no HH do armador do que a de 62.
 *
 * A peça importada da planilha por peça não entra aqui (F10): o número dela já vem pronto, e quem
 * monta a conta tira as importadas antes (`semPecasImportadas`), como no medido.
 */

/** Os elementos da fundação que repartem a medida peça a peça. A estaca tem a dela (Fase 39). */
export type FoundationElement = 'BLOCO' | 'BALDRAME' | 'RADIER';

/**
 * A escada, o muro e o pilar de contenção (Fase 42, etapa 3 — G3; RN-571, RN-572): peças da
 * família da estaca que repartem a medida peça a peça com o fator e a taxa próprios, sem tipo.
 */
export type StructurePieceElement = 'ESCADA' | 'MURO_CONTENCAO' | 'PILAR_CONTENCAO';

/**
 * Todo elemento que reparte a medida peça a peça por esta conta: a fundação e, desde a Fase 42,
 * o pilar e a viga (RN-564; `structure-factors.ts`) e a escada e a contenção (RN-571) — mesma
 * conta, outra peça.
 */
export type PieceElement = FoundationElement | StructuralElementKind | StructurePieceElement;

const POR_PECA: ReadonlySet<string> = new Set<PieceElement>([
  'BLOCO',
  'BALDRAME',
  'RADIER',
  'PILAR',
  'VIGA',
  'ESCADA',
  'MURO_CONTENCAO',
  'PILAR_CONTENCAO',
]);

/** A medida de uma peça em cada grandeza que o elemento dela mede. */
export interface FoundationPieceMeasure<E extends PieceElement = FoundationElement> {
  readonly pieceId: string;
  readonly element: E;
  readonly measures: Readonly<Partial<Record<MeasureBy, number>>>;
  /**
   * A taxa de aço que vale nesta peça, quando não é a da disciplina (Fase 41, etapa 2 — RN-544):
   * a própria, ou a do tipo de bloco. Ausente: a da disciplina.
   */
  readonly steelRates?: readonly SteelRate[];
}

/**
 * O que a conta precisa: a medida de **todas** as peças da fundação da tipologia — a parte de
 * cada uma sai da soma delas — e o fator que vale nas que têm, pelo id e por `ownFactorKey`.
 */
export interface FoundationFactors<E extends PieceElement = PieceElement> {
  readonly pieces: readonly FoundationPieceMeasure<E>[];
  readonly factors: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

/**
 * As peças de duas contas numa só (Fase 42): a fundação e a estrutura da mesma tipologia. Os ids
 * não se repetem — são de peças diferentes —, e cada serviço só lê as do elemento dele.
 */
export function joinPieceFactors(
  ...partes: readonly (FoundationFactors | undefined)[]
): FoundationFactors | undefined {
  const cheias = partes.filter((parte): parte is FoundationFactors => parte !== undefined);
  if (cheias.length <= 1) return cheias[0];
  return {
    pieces: cheias.flatMap((parte) => parte.pieces),
    factors: Object.assign({}, ...cheias.map((parte) => parte.factors)) as Readonly<
      Record<string, Readonly<Record<string, number>>>
    >,
  };
}

const viva = (peca: { readonly deletedAt?: string }): boolean => peca.deletedAt === undefined;

/**
 * O fator que vale num bloco, item a item: a composição do tipo, com o próprio do card por cima
 * (ADR-185). O item que nenhum dos dois tem fica de fora, e a conta usa o índice da obra.
 */
export function pileCapFactors(
  cap: Pick<PileCap, 'pileCapTypeId' | 'factors'>,
  types: ReadonlyMap<string, Pick<PileCapType, 'rates' | 'deletedAt'>>,
): Readonly<Record<string, number>> {
  const tipo = cap.pileCapTypeId === undefined ? undefined : types.get(cap.pileCapTypeId);
  const doTipo = tipo === undefined || !viva(tipo) ? {} : ownFactorsByKey(tipo.rates);
  return { ...doTipo, ...ownFactorsByKey(cap.factors) };
}

/** A lista que vale: a vazia é "nenhuma", e quem chama passa para a de baixo. */
const cheia = (taxas: readonly SteelRate[] | undefined): readonly SteelRate[] | undefined =>
  taxas === undefined || taxas.length === 0 ? undefined : taxas;

/**
 * A taxa de aço que vale num bloco (RN-544): a própria; sem ela, a do tipo. Ausente: a da
 * disciplina. A lista vale inteira — misturar bitolas de duas taxas daria um aço que ninguém
 * calculou.
 */
export function pileCapSteelRates(
  cap: Pick<PileCap, 'pileCapTypeId' | 'steelRates'>,
  types: ReadonlyMap<string, Pick<PileCapType, 'steelRates' | 'deletedAt'>>,
): readonly SteelRate[] | undefined {
  const tipo = cap.pileCapTypeId === undefined ? undefined : types.get(cap.pileCapTypeId);
  return (
    cheia(cap.steelRates) ??
    (tipo === undefined || !viva(tipo) ? undefined : cheia(tipo.steelRates))
  );
}

/**
 * O fator e a taxa de aço de cada peça da fundação desta tipologia — e, desde a Fase 42 (etapa
 * 3), da escada, do muro e do pilar de contenção —, com a medida de todas. `undefined` quando
 * nenhuma peça viva tem fator nem taxa — nem tipo com composição ou taxa, nem próprio: aí nada
 * muda, e a conta não precisa ver peça nenhuma.
 */
export function foundationFactorsOf(input: {
  readonly pileCaps?: readonly PileCap[];
  readonly groundBeams?: readonly GroundBeam[];
  readonly slabs?: readonly Slab[];
  readonly pileCapTypes?: readonly PileCapType[];
  readonly stairs?: readonly Stair[];
  readonly retainingWalls?: readonly RetainingWall[];
  readonly retainingColumns?: readonly RetainingColumn[];
}): FoundationFactors | undefined {
  const base = joinPieceFactors(foundationPieces(input), stairAndRetainingPieces(input))!;
  const nada =
    Object.keys(base.factors).length === 0 &&
    !base.pieces.some((peca) => peca.steelRates !== undefined);
  return nada ? undefined : base;
}

/**
 * A medida, o fator e a taxa de cada escada, muro e pilar de contenção vivos da tipologia —
 * sempre, mesmo sem fator nem taxa (Fase 42, etapa 3 — G3; RN-571, RN-572). Sem tipo: só o
 * próprio do card. A medida de cada peça é a da conta dela (`stairTotals`,
 * `retainingWallTotals`, `retainingColumnTotals`): o muro desconta os pilares da mesma
 * tipologia encaixados nele, e o pilar perde as faces dentro do muro (RN-372) — por isso cada
 * um recebe a lista inteira do outro. Fica à parte de `foundationPieces`, que a média da
 * fundação lê (ADR-186) e que segue só com o bloco, o baldrame e o radier.
 */
export function stairAndRetainingPieces(input: {
  readonly stairs?: readonly Stair[];
  readonly retainingWalls?: readonly RetainingWall[];
  readonly retainingColumns?: readonly RetainingColumn[];
}): FoundationFactors<StructurePieceElement> {
  const escadas = (input.stairs ?? []).filter(viva);
  const muros = (input.retainingWalls ?? []).filter(viva);
  const pilares = (input.retainingColumns ?? []).filter(viva);

  const factors: Record<string, Readonly<Record<string, number>>> = {};
  const comTaxa = (peca: { readonly steelRates?: readonly SteelRate[] }) => {
    const taxa = cheia(peca.steelRates);
    return taxa === undefined ? {} : { steelRates: taxa };
  };
  const guardar = (peca: { readonly id: string; readonly factors?: readonly ItemFactor[] }) => {
    const fatores = ownFactorsByKey(peca.factors);
    if (Object.keys(fatores).length > 0) factors[peca.id] = fatores;
  };
  escadas.forEach(guardar);
  muros.forEach(guardar);
  pilares.forEach(guardar);

  const pieces: FoundationPieceMeasure<StructurePieceElement>[] = [
    ...escadas.map((escada): FoundationPieceMeasure<StructurePieceElement> => {
      const t = stairTotals([escada]);
      return {
        pieceId: escada.id,
        element: 'ESCADA',
        measures: { VOLUME: t.volume, FACE: t.formwork, AREA: t.projection, COUNT: t.count },
        ...comTaxa(escada),
      };
    }),
    ...muros.map((muro): FoundationPieceMeasure<StructurePieceElement> => {
      const t = retainingWallTotals([muro], pilares);
      return {
        pieceId: muro.id,
        element: 'MURO_CONTENCAO',
        measures: { VOLUME: t.volume, FACE: t.face, LENGTH: t.length, COUNT: t.count },
        ...comTaxa(muro),
      };
    }),
    ...pilares.map((pilar): FoundationPieceMeasure<StructurePieceElement> => {
      const t = retainingColumnTotals([pilar], muros);
      return {
        pieceId: pilar.id,
        element: 'PILAR_CONTENCAO',
        measures: { VOLUME: t.volume, FACE: t.faces, LENGTH: t.height, COUNT: t.count },
        ...comTaxa(pilar),
      };
    }),
  ];
  return { pieces, factors };
}

/**
 * A medida, o fator e a taxa de cada peça viva da fundação — sempre, mesmo sem fator nem taxa. É o
 * que a média da fundação lê no servidor (Fase 41, etapa 3 — ADR-186), com as mesmas contas.
 */
export function foundationPieces(input: {
  readonly pileCaps?: readonly PileCap[];
  readonly groundBeams?: readonly GroundBeam[];
  readonly slabs?: readonly Slab[];
  readonly pileCapTypes?: readonly PileCapType[];
}): FoundationFactors<FoundationElement> {
  const blocos = (input.pileCaps ?? []).filter(viva);
  const baldrames = (input.groundBeams ?? []).filter(viva);
  const radiers = (input.slabs ?? []).filter((slab) => viva(slab) && slab.kind === 'RADIER');
  const tipos = new Map((input.pileCapTypes ?? []).map((tipo) => [String(tipo.id), tipo]));

  const factors: Record<string, Readonly<Record<string, number>>> = {};
  const guardar = (id: string, fatores: Readonly<Record<string, number>>) => {
    if (Object.keys(fatores).length > 0) factors[id] = fatores;
  };
  blocos.forEach((cap) => guardar(cap.id, pileCapFactors(cap, tipos)));
  baldrames.forEach((beam) => guardar(beam.id, ownFactorsByKey(beam.factors)));
  radiers.forEach((slab) => guardar(slab.id, ownFactorsByKey(slab.factors)));
  const taxas = new Map<string, readonly SteelRate[]>();
  const taxaDe = (id: string, lista: readonly SteelRate[] | undefined) => {
    if (lista !== undefined) taxas.set(id, lista);
  };
  blocos.forEach((cap) => taxaDe(cap.id, pileCapSteelRates(cap, tipos)));
  baldrames.forEach((beam) => taxaDe(beam.id, cheia(beam.steelRates)));
  radiers.forEach((slab) => taxaDe(slab.id, cheia(slab.steelRates)));
  const comTaxa = (id: string) => (taxas.has(id) ? { steelRates: taxas.get(id)! } : {});

  // O baldrame desconta o trecho dentro dos blocos (RN-368): a medida dele é a livre, com todos.
  const pieces: FoundationPieceMeasure[] = [
    ...blocos.map((cap): FoundationPieceMeasure => {
      const t = pileCapTotals([cap]);
      return {
        pieceId: cap.id,
        element: 'BLOCO',
        measures: { VOLUME: t.volume, FACE: t.faces, BOTTOM: t.bottom, COUNT: t.count },
        ...comTaxa(cap.id),
      };
    }),
    ...baldrames.map((beam): FoundationPieceMeasure => {
      const t = groundBeamTotals([beam], blocos);
      return {
        pieceId: beam.id,
        element: 'BALDRAME',
        measures: {
          VOLUME: t.volume,
          FACE: t.faces,
          BOTTOM: t.bottom,
          LENGTH: t.length,
          COUNT: t.count,
        },
        ...comTaxa(beam.id),
      };
    }),
    ...radiers.map((slab): FoundationPieceMeasure => {
      const t = slabTotals([slab], 'RADIER');
      return {
        pieceId: slab.id,
        element: 'RADIER',
        measures: { VOLUME: t.volume, AREA: t.area, CONTOUR: t.edge, COUNT: t.count },
        ...comTaxa(slab.id),
      };
    }),
  ];
  return { pieces, factors };
}

/**
 * Os kg de aço de uma peça da fundação (RN-545): o volume × a soma das bitolas da taxa que vale
 * nela — a própria ou a do tipo; sem elas, a da disciplina. Zero na peça sem volume ou sem taxa.
 *
 * É a mesma conta na tela (a parte da peça no serviço de aço) e na média do servidor (a medida da
 * peça desenhada no serviço de aço), para as duas darem o mesmo número.
 */
export function foundationPieceSteel(
  peca: Pick<FoundationPieceMeasure, 'measures' | 'steelRates'>,
  disciplina: readonly SteelRate[] | undefined,
): number {
  const volume = peca.measures.VOLUME ?? 0;
  if (!(volume > 0)) return 0;
  const taxa = (peca.steelRates ?? disciplina ?? []).reduce(
    (soma, linha) => soma + linha.kgPerM3,
    0,
  );
  return taxa > 0 ? volume * taxa : 0;
}

/**
 * Quanto da medida de um serviço da fundação é de cada peça, pelo id dela (RN-539).
 *
 * O serviço de aço reparte pelo **aço** de cada peça (RN-556): o medido dele é Σ volume × taxa,
 * peça a peça (RN-545), e o item sem bitola — o HH do armador, o arame — leva em cada peça a parte
 * do aço dela, e não a do volume. Sem taxa de peça nem de tipo no elemento, todas usam a da
 * disciplina (`disciplina`), e o aço reparte como o volume: aí a parte é a do volume, a conta de
 * sempre (ADR-187).
 *
 * Vazio no serviço que não mede bloco, baldrame, radier, pilar ou viga, e quando nenhuma peça mede.
 */
export function foundationShares(
  service: WithElement,
  base: Pick<FoundationFactors, 'pieces'>,
  disciplina?: readonly SteelRate[],
): Readonly<Record<string, number>> {
  const elemento = serviceElement(service);
  if (elemento === undefined || !POR_PECA.has(elemento)) return {};
  const doElemento = base.pieces.filter((peca) => peca.element === elemento);
  const peloAco =
    service.measureBy === 'ACO' && doElemento.some((peca) => peca.steelRates !== undefined);
  const grandeza: MeasureBy = service.measureBy === 'ACO' ? 'VOLUME' : service.measureBy;
  const medidas = doElemento.flatMap((peca) => {
    const medida = peloAco ? foundationPieceSteel(peca, disciplina) : peca.measures[grandeza];
    return medida === undefined || !(medida > 0) ? [] : [[peca.pieceId, medida] as const];
  });
  const total = medidas.reduce((soma, [, medida]) => soma + medida, 0);
  if (total <= 0) return {};
  return Object.fromEntries(medidas.map(([id, medida]) => [id, medida / total]));
}

/**
 * A taxa de aço equivalente de um serviço da fundação nesta tipologia (Fase 41, etapa 2 — RN-545,
 * RN-546, ADR-187): bitola a bitola, Σ (volume da peça × taxa que vale nela) ÷ Σ volume.
 *
 * Com ela, o resto da conta fica como era: o aço medido é o volume total × a soma das bitolas, que
 * é Σ volume × taxa peça a peça (RN-545), e a participação de cada bitola sai da mesma lista
 * (RN-546). A peça sem taxa própria nem do tipo usa a da disciplina; a sem nenhuma entra com zero.
 *
 * `undefined` quando o serviço não mede bloco, baldrame, radier, pilar ou viga, ou quando nenhuma peça do
 * elemento tem taxa própria nem do tipo: aí vale a da disciplina, sem conta nenhuma.
 */
export function foundationSteelRates(
  service: WithElement,
  base: Pick<FoundationFactors, 'pieces'> | undefined,
  disciplina: readonly SteelRate[] | undefined,
): readonly SteelRate[] | undefined {
  if (base === undefined) return undefined;
  const elemento = serviceElement(service);
  if (elemento === undefined || !POR_PECA.has(elemento)) return undefined;
  const pecas = base.pieces.filter(
    (peca) => peca.element === elemento && (peca.measures.VOLUME ?? 0) > 0,
  );
  if (!pecas.some((peca) => peca.steelRates !== undefined)) return undefined;
  const volume = pecas.reduce((soma, peca) => soma + (peca.measures.VOLUME ?? 0), 0);
  return STEEL_GAUGES.flatMap(({ code }): SteelRate[] => {
    const kg = pecas.reduce((soma, peca) => {
      const taxa = (peca.steelRates ?? disciplina ?? []).find((linha) => linha.gauge === code);
      return soma + (peca.measures.VOLUME ?? 0) * (taxa?.kgPerM3 ?? 0);
    }, 0);
    return kg > 0 ? [{ gauge: code, kgPerM3: kg / volume }] : [];
  });
}
